/**
 * Shared vocabulary for the whole system: enum string values, scalar aliases,
 * and the wire shapes the API returns.
 *
 * This package is pure — no HTTP, no env, no DB client (CLAUDE.md §5).
 * Enum values are snake_case strings (§6) and are the single source of truth
 * shared by the Drizzle schema, the zod request schemas, and the frontend.
 */

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/**
 * A Postgres `numeric` rendered as a decimal string, e.g. `'1234.5600'`.
 *
 * Money and quantities are NEVER `number` anywhere in this system (§3.10).
 * Crossing a JS float would silently lose cents on the way to a pricing decision.
 */
export type NumericString = string

/** Calendar date, `YYYY-MM-DD`. No time component, no timezone. */
export type IsoDate = string

/** Instant, ISO 8601 with offset. */
export type IsoTimestamp = string

/** UUID generated at the point of entry, in the browser (§3.2). */
export type ClientUuid = string

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const BATCH_STATUS = ['rearing', 'laying', 'closed'] as const
export type BatchStatus = (typeof BATCH_STATUS)[number]

export const BIRD_EVENT_TYPE = [
  'arrival',
  'mortality',
  'cull',
  'sale',
  'transfer_in',
  'transfer_out',
  'adjustment',
] as const
export type BirdEventType = (typeof BIRD_EVENT_TYPE)[number]

/** Event types that must carry a positive `qtyDelta`. */
export const INBOUND_BIRD_EVENT_TYPE: readonly BirdEventType[] = ['arrival', 'transfer_in']

/** Event types that must carry a negative `qtyDelta`. */
export const OUTBOUND_BIRD_EVENT_TYPE: readonly BirdEventType[] = [
  'mortality',
  'cull',
  'sale',
  'transfer_out',
]

/** Losses counted in `mortality_pct` (§5.7). Sales and transfers are not losses. */
export const MORTALITY_BIRD_EVENT_TYPE: readonly BirdEventType[] = ['mortality', 'cull']

export const MATERIAL_TYPE = ['ingredient', 'finished_feed', 'supplement'] as const
export type MaterialType = (typeof MATERIAL_TYPE)[number]

export const MOVEMENT_SOURCE_TYPE = [
  'purchase',
  'formulation_input',
  'formulation_output',
  'feed_issue',
  'adjustment',
] as const
export type MovementSourceType = (typeof MOVEMENT_SOURCE_TYPE)[number]

export const COST_TYPE = ['labor', 'overhead', 'other'] as const
export type CostType = (typeof COST_TYPE)[number]

export const COST_ACTIVITY = [
  'feeding',
  'vaccination',
  'medical',
  'handling',
  'egg_collection',
  'utilities',
  'housing',
  'depreciation',
  'other',
] as const
export type CostActivity = (typeof COST_ACTIVITY)[number]

export const MEDICAL_CATEGORY = ['medicine', 'vet', 'supplement', 'other'] as const
export type MedicalCategory = (typeof MEDICAL_CATEGORY)[number]

export const ISSUE_OUTCOME = ['resolved', 'partial', 'unresolved', 'ongoing'] as const
export type IssueOutcome = (typeof ISSUE_OUTCOME)[number]

export const PRODUCTION_PCT_BASIS = ['hen_day', 'hen_housed'] as const
export type ProductionPctBasis = (typeof PRODUCTION_PCT_BASIS)[number]

export const FARM_ROLE = ['owner', 'manager', 'viewer'] as const
export type FarmRole = (typeof FARM_ROLE)[number]

// ---------------------------------------------------------------------------
// Cost wire shapes
// ---------------------------------------------------------------------------

/**
 * Every cost figure is reported twice: with overhead allocated in, and without.
 * Overhead allocation is defensible but contested, and the farm must be able to
 * see both (§3.6). There is no plain `value` field on purpose.
 */
export interface OverheadPair {
  inclOverhead: NumericString
  exclOverhead: NumericString
}

/** Accumulated batch cost broken out by component (§5.5). */
export interface BatchCostBreakdown {
  chickCost: NumericString
  feedCost: NumericString
  vaccinationCost: NumericString
  medicalCost: NumericString
  laborCost: NumericString
  overheadCost: NumericString
  otherCost: NumericString
  salvageCredit: NumericString
  accumulatedCost: OverheadPair
  population: number
  /** Null when the batch has no live birds — a division nobody should be shown. */
  costPerBird: OverheadPair | null
}

/**
 * Cost per egg is always two numbers (§3.5).
 *
 * `cash` answers "is this flock cash-positive today?".
 * `loaded` answers "did this flock pay back its rearing investment?".
 *
 * There is deliberately no single `costPerEgg` field: accumulated cost divided
 * by eggs-to-date is a meaningless figure in week 20 and must never be surfaced.
 */
export interface CostPerEgg {
  /** Null before the flock lays its first recorded egg. Zero would read as "free eggs". */
  cash: OverheadPair | null
  loaded: OverheadPair | null
  /** The rearing component folded into `loaded`, exposed so the split is visible. */
  rearingPerExpectedEgg: OverheadPair
}

/** Frozen at `layer_start_date` and used as the basis for both cost-per-egg figures (§5.6). */
export interface LayerSnapshot {
  layerStartDate: IsoDate
  birdsAtLayerStart: number
  rearingCostTotal: OverheadPair
  costPerLayerBird: OverheadPair | null
}

// ---------------------------------------------------------------------------
// Data quality
// ---------------------------------------------------------------------------

/**
 * Surfaced by the costing engine and the nightly data-quality job rather than
 * blocking a write. Back-dated entry is legitimate farm behaviour (§3.9).
 */
export interface DataQualityFlag {
  code:
    | 'negative_stock'
    | 'missing_unit_cost'
    | 'unvalued_issue'
    | 'no_active_batches'
    | 'missing_salvage_estimate'
    | 'production_drop'
  message: string
  materialId?: string
  batchId?: string
  date?: IsoDate
  value?: NumericString
}

// ---------------------------------------------------------------------------
// Wire records
// ---------------------------------------------------------------------------
//
// What the API actually returns. These live here rather than in `@henlytics/db`
// so the browser never imports a Drizzle schema — the ORM has no business in the
// bundle the farm downloads.

export interface BatchRecord {
  id: string
  farmId: string
  code: string
  breed: string | null
  supplier: string | null
  arrivalDate: IsoDate
  chicksReceived: number
  chickAgeDaysOnArrival: number
  chickUnitCost: NumericString | null
  chickTotalCost: NumericString
  initialWeightG: number | null
  layerStartDateOverride: IsoDate | null
  status: BatchStatus
  clientUuid: ClientUuid
  createdAt: IsoTimestamp
}

export interface BatchDetailRecord extends BatchRecord {
  /** Derived from config and the override — never stored (§3.3). */
  layerStartDate: IsoDate
}

export interface BirdEventRecord {
  id: string
  batchId: string
  eventDate: IsoDate
  eventType: BirdEventType
  qtyDelta: number
  reason: string | null
  salvageAmount: NumericString | null
  notes: string | null
  clientUuid: ClientUuid
  createdAt: IsoTimestamp
  voidedAt: IsoTimestamp | null
  voidedByEventId: string | null
}

export interface MaterialRecord {
  id: string
  farmId: string
  name: string
  materialType: MaterialType
  uom: string
  isActive: boolean
}

export interface FeedIssueRecord {
  id: string
  batchId: string
  materialId: string
  issueDate: IsoDate
  qty: NumericString
  clientUuid: ClientUuid
  createdAt: IsoTimestamp
}

export interface CostEntryRecord {
  id: string
  farmId: string
  batchId: string | null
  costDate: IsoDate
  costType: CostType
  activity: CostActivity
  amount: NumericString
  hours: NumericString | null
  periodStart: IsoDate
  periodEnd: IsoDate
  notes: string | null
  clientUuid: ClientUuid
  createdAt: IsoTimestamp
  voidedAt: IsoTimestamp | null
}

export interface EggProductionRecord {
  id: string
  batchId: string
  productionDate: IsoDate
  eggsProduced: number
  eggsBroken: number
  eggsDirty: number
  notes: string | null
  clientUuid: ClientUuid
  createdAt: IsoTimestamp
  voidedAt: IsoTimestamp | null
}

export interface EggSaleRecord {
  id: string
  farmId: string
  batchId: string | null
  saleDate: IsoDate
  grade: string | null
  qtyEggs: number
  unitPrice: NumericString
  totalAmount: NumericString
  customer: string | null
  clientUuid: ClientUuid
  createdAt: IsoTimestamp
  voidedAt: IsoTimestamp | null
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export interface BatchCostReportRecord {
  batchId: string
  code: string
  status: BatchStatus
  asOf: IsoDate
  ageDays: number
  layerStartDate: IsoDate
  isLaying: boolean
  cost: BatchCostBreakdown
  layer: LayerSnapshot
  costPerEgg: CostPerEgg
  production: {
    eggsOnDay: number
    eggsToDate: number
    eggsSinceLayerStart: number
    eggsLast7Days: number
    population: number
    productionPct: NumericString | null
    mortalityPct: NumericString | null
  }
  profitability: {
    revenue: NumericString
    margin: OverheadPair
    marginPerEgg: OverheadPair | null
  }
  flags: DataQualityFlag[]
}

export interface DashboardRecord {
  asOf: IsoDate
  currencyCode: string
  totals: {
    activeBatches: number
    livingBirds: number
    eggsToday: number
    eggsLast7Days: number
  }
  batches: BatchCostReportRecord[]
  flags: DataQualityFlag[]
}

export interface SessionRecord {
  userId: string
  farmId: string
  role: FarmRole
}

// ---------------------------------------------------------------------------
// API envelope
// ---------------------------------------------------------------------------

export interface ApiError {
  error: {
    code: string
    message: string
    details?: unknown
  }
}

export type ApiResult<T> = { data: T } | ApiError

// ---------------------------------------------------------------------------
// Farm settings
// ---------------------------------------------------------------------------

export * from './farm-settings'
